import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Drawer,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  InfiniteHitsProvided,
  StateResultsProvided,
} from 'react-instantsearch-core'
import {
  Configure,
  connectInfiniteHits,
  connectStateResults,
  InstantSearch,
} from 'react-instantsearch-dom'
import {
  Fade,
  IconButton,
  Icons,
  InformationModal,
  SkeletonText,
  SNSIconButton,
} from 'utoniq-ui'

import {
  Artist,
  ArtistBanner,
  ArtistTopQuery,
  EventsByArtistConnectionFragment,
  GachaBoxFragment,
  LiveStream,
  Maybe,
  MembershipSubscription,
  MembershipSubscriptionPlanStatus,
  MissionBoxFragment,
  OrganizationNotificationBoxFragment,
  OrganizationNotificationConnectionFragment,
  PostBoxFragment,
  RankingUser,
  StoreItemFragment,
  ThreadBoxFragment,
} from '@/clients/utoniq-core/schema'
import {
  BaseLayout,
  Head,
  Header,
  HeaderImageArea,
  SideMenu,
  SideMenuProps,
} from '@/components/layouts'
import {
  EventCard,
  EventCardSkeleton,
  GachaBox,
  GachaBoxSkeleton,
  MissionBox,
  MissionBoxSkeleton,
  PlanLabel,
  PostBox,
  PostBoxSkeleton,
  StoreItemArea,
  StoreItemAreaSkeleton,
  ThreadBox,
  ThreadBoxSkeleton,
  UnregisteredInfoLabel,
} from '@/components/organisms'
import { HorizontalList } from '@/components/organisms/functionals'
import {
  ArtistThemeProvider,
  styles,
  styleSets,
  useArtistThemeContext,
} from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { App } from '@/core/native'
import * as algolia from '@/lib/algolia'
import { usePrivateAction } from '@/lib/privateAction'
export * from './fragments'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import { Rank } from '@/components/organisms/ranking/Rank'
import { individualHideArtistNewPosts } from '@/core/individual'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import { isExternalLink, replaceParameters } from '@/lib/url'

import { NotFound } from '../../Errors/NotFound'
export type State = {
  loading: boolean
  artist: ArtistTopQuery['artist']
  artistId: string
  liveStream: Maybe<Pick<LiveStream, 'id' | 'isPremium'>>
  newPosts: PostBoxFragment[]
  banners: Pick<ArtistBanner, 'thumbnail' | 'link'>[]
  newMissions: MissionBoxFragment[]
  newGachas: GachaBoxFragment[]
  membership: Pick<MembershipSubscription, 'products'> | null
  memberArtists: ArtistCellProps['memberArtists']
  followArtists: ArtistCellProps['followArtists']
  events: Maybe<EventsByArtistConnectionFragment>
  user: ArtistCellProps['user']
  isArtist: ArtistCellProps['isArtist']
  newThreads: ThreadBoxFragment[]
  isBlocked: boolean
  myRanking: Maybe<Pick<RankingUser, 'rank' | 'userId'>>
  hasMission: boolean
  notifications: Maybe<OrganizationNotificationConnectionFragment['edges']>
}

export type Action = {
  onFollow: () => Promise<unknown>
  onUnfollow: () => Promise<unknown>
  onLikePost: (
    postId: string,
    isLike: boolean,
    likeCount: number
  ) => Promise<void>
  onClickShare: () => Promise<void>
  onUnBlock: () => Promise<void>
}

const client = algolia.client()

export const ArtistTopTemplate: Template<State, Action> = ({
  state,
  onFollow,
  onUnfollow,
  onLikePost,
  onClickShare,
  onUnBlock,
}) => {
  const { t } = useTranslation('artistTop')
  const publishedAtTimestampRef = useRef<number>(DateTime.nowUnixMilliseconds())
  const { analytics } = useAnalytics()
  const itemResultRef = useRef<{ items: StoreItemFragment[]; page: number }>({
    items: [],
    page: 1,
  })

  const { routerNames } = useRouter()

  const isActive = useMemo(() => {
    return state.membership?.products?.some(product =>
      product?.plans?.some(
        plan => plan.status === MembershipSubscriptionPlanStatus.Active
      )
    )
  }, [state.membership?.products])

  const membershipContent = useMemo(() => {
    const product = state.membership?.products?.find(product =>
      product?.plans?.some(plan =>
        state.artist?.patron?.subscription?.planId
          ? plan.planId === state.artist?.patron?.subscription?.planId
          : plan.status === MembershipSubscriptionPlanStatus.Active
      )
    )
    return product?.content
  }, [state.artist?.patron, state.membership?.products])

  const handleOnCompleted = useCallback(
    (items: StoreItemFragment[], { page }) => {
      itemResultRef.current = { items, page }
      analytics?.track('view_item_list', {
        item_list_id: 'artist_store_list',
        item_list_name: 'Artist Store Items',
        items: items.map(item => ({
          item_id: item.id,
          item_name: item.title,
          item_category: 'store_item',
          item_category2: item.type,
          item_category3: App.getPlatform(),
          item_category4: page,
          item_category5: state.artistId,
          price: item.price,
        })),
      })
    },
    [analytics, state.artistId]
  )

  const handleSelectItem = useCallback(() => {
    analytics?.track('select_item', {
      item_list_id: `artist_store_list_${state.artistId}`,
      item_list_name: 'Artist Store Items',
      items: itemResultRef.current.items.map(item => ({
        item_id: item.id,
        item_name: item.title,
        item_category: 'store_item',
        item_category2: item.type,
        item_category3: App.getPlatform(),
        item_category4: itemResultRef.current.page,
        item_category5: state.artistId,
        price: item.price,
      })),
    })
  }, [analytics, state.artistId])

  const handleToList = useCallback(() => {
    analytics?.track('select_item', {
      item_list_id: `artist_store_list_related_${state.artistId}`,
      item_list_name: 'Artist Store Related Items',
      items: itemResultRef.current.items.map(item => ({
        item_id: item.id,
        item_name: item.title,
        item_category: 'store_item',
        item_category2: item.type,
        item_category3: App.getPlatform(),
        item_category4: itemResultRef.current.page,
        item_category5: state.artistId,
        price: item.price,
      })),
    })
  }, [analytics, state.artistId])

  return !(!state.loading && state.artist === null) ? (
    <ArtistThemeProvider>
      <BaseLayout
        safeArea={{ pt: 0 }}
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStream?.id,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
      >
        <Head title={[routerNames.ArtistTop, state.artist?.displayName]} />
        {state?.isBlocked ? (
          <>
            <BlockedArtistCell
              loading={state.loading}
              artist={state.artist}
              user={state.user}
              isArtist={state.isArtist}
              followArtists={state.followArtists}
              memberArtists={state.memberArtists}
              onUnBlock={onUnBlock}
            />
            <Typography variant="body2" sx={{ px: 5, py: 3 }}>
              {t('blockedArtistDescription')}
            </Typography>

            {/**
             * @todo
             * ブロック解除API本格実装後コメントアウト
             * https://app.clickup.com/t/908127/UT-8682
             * <TextLink variant="subtitle1" sx={{ px: 5, py: 3 }}>
             *  {t('toBlockList')}
             * </TextLink>
             */}
          </>
        ) : (
          <>
            <ArtistCell
              loading={state.loading}
              artist={state.artist}
              artistId={state.artistId}
              onFollow={onFollow}
              onUnfollow={onUnfollow}
              user={state.user}
              isArtist={state.isArtist}
              liveStream={state.liveStream}
              followArtists={state.followArtists}
              memberArtists={state.memberArtists}
              isMember={!!state.artist?.patron}
              myRanking={state.myRanking}
              hasMission={state.hasMission}
            />

            {state.artist && (
              <IntroductionCell
                id={state.artist.id}
                profileText={state.artist.profileText}
                displayName={state.artist.displayName}
                sns={state.artist.sns}
                textLink={state.artist.textLink}
                onClickShare={onClickShare}
              />
            )}
            <BannersCell
              loading={state.loading}
              banners={state.banners}
              user={state.user}
            />
            {isActive && (
              <MembershipCell
                loading={state.loading}
                membership={state.membership}
                artistId={state.artist?.id}
                content={membershipContent}
                isMember={!!state.artist?.patron}
                isPersonalInformationRequired={
                  !!state.artist?.patron?.isPersonalInformationRequired
                }
                isMembershipPersonalInformationRequired={
                  !!state.user?.isMembershipPersonalInformationRequired
                }
              />
            )}
            {!!state.notifications?.length && (
              <NotificationListsCell
                notifications={state.notifications}
                artistId={state?.artistId}
              />
            )}
            <NewEventsCell
              artistId={state.artistId}
              loading={state.loading}
              events={state.events}
            />
            {!individualHideArtistNewPosts() && (
              <NewPostsCell
                loading={state.loading}
                newPosts={state.newPosts}
                artistId={state.artistId}
                onLikePost={onLikePost}
                isMembershipPersonalInformationRequired={
                  !!state.user?.isMembershipPersonalInformationRequired
                }
              />
            )}
            <NewThreadsCell
              loading={state.loading}
              newThreads={state.newThreads}
              artistId={state.artistId}
              isMember={!!state.artist?.patron}
              isShowModalPersonalInfoRequire={
                !!(
                  state?.user?.isMembershipPersonalInformationRequired &&
                  state?.artist?.patron?.isPersonalInformationRequired
                )
              }
            />
            {config.modules.store && config.modules.item && (
              <InstantSearch
                indexName={algolia.indexes.items}
                searchClient={client}
              >
                <Configure
                  hitsPerPage={10}
                  filters={algolia.filters.itemStoreArtist(
                    state.artistId,
                    publishedAtTimestampRef.current
                  )}
                />
                <NewStoresCellWithAlgolia
                  loading={state.loading}
                  artistId={state.artistId}
                  onSelectItem={handleSelectItem}
                  onToList={handleToList}
                />
                <StoreStateResults onCompleted={handleOnCompleted} />
              </InstantSearch>
            )}
            <NewMissionsCell
              loading={state.loading}
              newMissions={state.newMissions}
              artistId={state.artistId}
            />
            {config.modules.gacha && (
              <NewGachasCell
                loading={state.loading}
                newGachas={state.newGachas}
                artistId={state.artistId}
              />
            )}
          </>
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  ) : (
    <NotFound useNextRouter={false} />
  )
}

type ArtistCellProps = {
  loading: State['loading']
  artist: State['artist']
  artistId: State['artistId']
  liveStream: State['liveStream']
  myRanking: State['myRanking']
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  followArtists: Maybe<SideMenuProps['followArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
  isMember: boolean
  onFollow: Action['onFollow']
  onUnfollow: Action['onUnfollow']
  hasMission: State['hasMission']
}
const ArtistCell: Cell<ArtistCellProps> = ({
  loading,
  artist,
  artistId,
  liveStream,
  followArtists,
  memberArtists,
  user,
  isArtist,
  isMember,
  onFollow,
  onUnfollow,
  myRanking,
  hasMission,
}) => {
  const [{ bgImage }] = useArtistThemeContext()

  if (loading) {
    return (
      <CellFrame>
        <Skeleton
          variant="rectangular"
          width="100%"
          sx={{
            height: '100vw',
            maxHeight: 744,
            pt: styles.safeAreaTop,
            '& header': { visibility: 'visible' },
          }}
        >
          <ArtistHeaderCell
            artist={artist}
            liveStream={liveStream}
            artistId={artistId}
            followArtists={followArtists}
            memberArtists={memberArtists}
            user={user}
            isArtist={isArtist}
            isMember={isMember}
            myRanking={myRanking}
            hasMission={hasMission}
          />
          <ArtistInfoCell
            artist={artist}
            artistId={artistId}
            onFollow={onFollow}
            onUnfollow={onUnfollow}
          />
        </Skeleton>
      </CellFrame>
    )
  }

  return (
    <>
      <HeaderImageArea
        hasSafeArea
        // グラデーションマスクを表示しないアプリを指定
        gradationMask={false}
        bgImage={bgImage}
        imageUri={artist?.avatar?.mediumUri}
      >
        <Fade sx={{ height: '100%' }} in>
          <ArtistHeaderCell
            artist={artist}
            liveStream={liveStream}
            artistId={artistId}
            followArtists={followArtists}
            memberArtists={memberArtists}
            user={user}
            isArtist={isArtist}
            isMember={isMember}
            myRanking={myRanking}
            hasMission={hasMission}
          />
        </Fade>
      </HeaderImageArea>
      <ArtistInfoCell
        artist={artist}
        artistId={artistId}
        onFollow={onFollow}
        onUnfollow={onUnfollow}
      />
    </>
  )
}

type ArtistHeaderCellProps = {
  artist: State['artist']
  liveStream: State['liveStream']
  artistId: State['artistId']
  myRanking: State['myRanking']
  followArtists: SideMenuProps['followArtists']
  memberArtists: SideMenuProps['memberArtists']
  user: SideMenuProps['user']
  isArtist: SideMenuProps['isArtist']
  isMember: boolean
  hasMission: State['hasMission']
}

const ArtistHeaderCell: Cell<ArtistHeaderCellProps> = ({
  artist,
  liveStream,
  artistId,
  myRanking,
  followArtists,
  memberArtists,
  user,
  isArtist,
  isMember,
  hasMission,
}) => {
  const { routerPages, generatePath } = useRouter()
  const { showModal } = useSettingUserInfoModal()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const [openMembershipOnlyModal, setOpenMembershipOnlyModal] =
    useState<boolean>(false)

  const liveStreamButtonAction = useMemo(() => {
    // メンバー限定配信ではない時
    if (!liveStream?.isPremium) {
      return {
        href: liveStream?.id
          ? generatePath(routerPages.LiveDetail, {
              liveStreamId: liveStream.id,
            })
          : undefined,
      }
    }
    // is member and user need enter information
    if (
      isMember &&
      user?.isMembershipPersonalInformationRequired &&
      artist?.patron?.isPersonalInformationRequired
    ) {
      return {
        onClick() {
          showModal()
        },
      }
    }
    // メンバー限定配信且つメンバーの時
    if (isMember) {
      return {
        href: liveStream?.id
          ? generatePath(routerPages.LiveDetail, {
              liveStreamId: liveStream.id,
            })
          : undefined,
      }
    }
    // メンバー限定配信且つメンバーではない時
    return {
      onClick: liveStream.isPremium
        ? () => setOpenMembershipOnlyModal(true)
        : undefined,
    }
  }, [
    generatePath,
    isMember,
    liveStream?.id,
    liveStream?.isPremium,
    routerPages.LiveDetail,
    user?.isMembershipPersonalInformationRequired,
    artist?.patron?.isPersonalInformationRequired,
  ])

  return (
    <Stack justifyContent="space-between" sx={{ height: '100%' }}>
      <Stack sx={{ pt: 2 }}>
        <Header
          color="transparent"
          elevation={0}
          position="absolute"
          sx={{ backgroundColor: 'transparent' }}
          left={
            config.applicationType === 'organization' && (
              <IconButton.AvatarNoImage
                edge="start"
                onClick={() => setOpenSideMenu(!openSideMenu)}
                badge={
                  user?.hasManagementTask || !!user?.unreadNotificationCount
                }
                color="icon.line"
                bgcolor="icon.background"
              />
            )
          }
          right={
            <Stack direction="row" spacing={3} alignItems="center">
              {liveStream?.id && (
                <Button
                  variant="contained"
                  color="like"
                  startIcon={<Icons.Live sx={{ color: 'white' }} />}
                  sx={{
                    color: 'white',
                    borderRadius: 1,
                    width: 82,
                    bgcolor: 'like.tap',
                  }}
                  {...liveStreamButtonAction}
                >
                  LIVE
                </Button>
              )}
              {myRanking?.rank && myRanking.rank <= 10 && (
                <ButtonBase
                  sx={{ width: 48, height: 48, borderRadius: 1 }}
                  href={generatePath(routerPages.ArtistRanking, { artistId })}
                >
                  <Rank rank={myRanking.rank} size="xSmall" />
                </ButtonBase>
              )}
              {
                /**
                 * @description
                 * 暫定対応でdiicaのみミッション一覧への動線追加
                 * https://app.clickup.com/t/908127/UT-9852
                 */
                hasMission && config.modules.mission && (
                  <IconButton.Mission
                    color="icon.line"
                    bgcolor="icon.background"
                    size="medium"
                    href={generatePath(routerPages.ArtistMissions, {
                      artistId,
                    })}
                  />
                )
              }
              {user && config.applicationType === 'standalone' && (
                <Stack direction="row" spacing={3}>
                  {config.modules.campaign && config.modules.mission && (
                    <IconButton.Event
                      size="medium"
                      href={generatePath(routerPages.GlobalEvents)}
                      color="icon.line"
                      bgcolor="icon.background"
                    />
                  )}
                  <IconButton.Notice
                    size="medium"
                    href={routerPages.Notifications}
                    color="icon.line"
                    bgcolor="icon.background"
                  />
                </Stack>
              )}
            </Stack>
          }
        />
        <SideMenu
          followArtists={followArtists}
          memberArtists={memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={user}
          isArtist={isArtist}
        />
      </Stack>
      <MembershipOnlyModalCell
        open={openMembershipOnlyModal}
        onClose={() => setOpenMembershipOnlyModal(false)}
        artistId={artistId}
        displayName={artist?.displayName || ''}
      />
    </Stack>
  )
}

type ArtistInfoCellProps = {
  artist: State['artist']
  artistId: State['artistId']
  onFollow: Action['onFollow']
  onUnfollow: Action['onUnfollow']
}

const ArtistInfoCell: Cell<ArtistInfoCellProps> = ({
  artist,
  artistId,
  onFollow,
  onUnfollow,
}) => {
  const { t } = useTranslation('artistTop')
  const { handleLoginModal } = usePrivateAction()
  const [openMembershipOnlyModal, setOpenMembershipOnlyModal] =
    useState<boolean>(false)

  return (
    <Stack
      justifyContent="space-between"
      flexDirection="row"
      alignItems="center"
      sx={{ mt: 4, mb: artist?.profileText ? 2 : 6 }}
    >
      {config.applicationType === 'organization' && (
        <>
          <Stack sx={{ pl: 5 }}>
            <Typography color="text.primary" variant="h2">
              {artist?.displayName}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {artist?.formattedAccountId}
            </Typography>
          </Stack>
          <Stack sx={{ pr: 5, ml: 4 }}>
            <Box>
              {artist?.follower ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLoginModal(onUnfollow)}
                  sx={{ px: 4, width: '100px' }}
                >
                  {t('following')}
                </Button>
              ) : (
                <Button
                  size="small"
                  onClick={handleLoginModal(onFollow)}
                  sx={{ px: 4, width: '100px' }}
                >
                  {t('follow')}
                </Button>
              )}
            </Box>
          </Stack>
        </>
      )}
      <MembershipOnlyModalCell
        open={openMembershipOnlyModal}
        onClose={() => setOpenMembershipOnlyModal(false)}
        artistId={artistId}
        displayName={artist?.displayName || ''}
      />
    </Stack>
  )
}

type IntroductionCellProps = Pick<
  Artist,
  'id' | 'introduction' | 'profileText' | 'sns' | 'textLink' | 'displayName'
> & {
  onClickShare: Action['onClickShare']
}
const IntroductionCell: Cell<IntroductionCellProps> = ({
  id,
  profileText,
  displayName,
  sns,
  textLink,
  onClickShare,
}) => {
  const { t } = useTranslation('artistTop')
  const [open, setOpen] = useState<boolean>(false)
  return (
    <CellFrame sx={{ px: 5, pb: profileText ? 6 : 0 }}>
      {profileText && (
        <Typography variant="body2" sx={styleSets.lineCamp(2)}>
          {profileText}
        </Typography>
      )}
      {!!profileText && (
        <Button
          variant="text"
          sx={{ p: 0, mt: 1 }}
          onClick={() => setOpen(true)}
        >
          {t('seeArtistProfile')}
        </Button>
      )}
      <Drawer open={open} onClose={() => setOpen(false)}>
        <Stack sx={{ pt: styles.safeAreaTop, px: 5, pb: 6 }}>
          <Stack direction="row" sx={{ py: 5 }} alignItems="center">
            <Stack flex={1}>
              <Typography width="100%" variant="h6" align="center">
                {displayName}
              </Typography>
            </Stack>
            <IconButton.Close
              edge="end"
              size="small"
              onClick={() => setOpen(false)}
            />
          </Stack>
          {!!profileText && (
            <Stack sx={{ pb: 6 }}>
              <Typography variant="body2">{profileText}</Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={4} sx={{ pb: 3 }}>
            {sns?.instagram && (
              <SNSIconButton.Instagram
                component="a"
                size="xLarge"
                disabled={!sns?.instagram}
                href={sns?.instagram ?? '#'}
                target="_blank"
              />
            )}
            {sns?.twitter && (
              <SNSIconButton.Twitter
                component="a"
                size="xLarge"
                disabled={!sns?.twitter}
                href={sns?.twitter ?? '#'}
                target="_blank"
              />
            )}
            {sns?.tiktok && (
              <SNSIconButton.Tiktok
                component="a"
                size="xLarge"
                disabled={!sns?.tiktok}
                href={sns?.tiktok ?? '#'}
                target="_blank"
              />
            )}
            {sns?.facebook && (
              <SNSIconButton.Facebook
                component="a"
                size="xLarge"
                disabled={!sns?.facebook}
                href={sns?.facebook ?? '#'}
                target="_blank"
              />
            )}
            {sns?.youtube && (
              <SNSIconButton.Youtube
                component="a"
                size="xLarge"
                disabled={!sns?.youtube}
                href={sns?.youtube ?? '#'}
                target="_blank"
              />
            )}
            <SNSIconButton.Share size="xLarge" onClick={onClickShare} />
          </Stack>

          <List>
            {!!textLink &&
              textLink?.length > 0 &&
              textLink?.map(textLink => {
                if (!textLink) return

                return (
                  <ListItemButton
                    sx={{ height: 52, px: 0 }}
                    divider
                    href={textLink?.uri || ''}
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Typography variant="body2">{textLink?.content}</Typography>
                  </ListItemButton>
                )
              })}
          </List>
        </Stack>
      </Drawer>
    </CellFrame>
  )
}

type BannersCellProps = Pick<State, 'banners' | 'loading' | 'user'>
const BannersCell: Cell<BannersCellProps> = ({ banners, loading, user }) => {
  return (
    <CellFrame sx={{ px: 5, pb: banners.length > 0 ? 6 : 0 }}>
      <Stack spacing={4}>
        {loading &&
          [1, 2].map(i => (
            <Skeleton variant="rectangular" height={140} key={i} />
          ))}
        {banners.map((b, i) => (
          <Fade key={i} in>
            <Card>
              {b.link?.externalUrl ? (
                <CardActionArea
                  component="a"
                  target={'_blank'}
                  href={
                    replaceParameters(b.link?.externalUrl, {
                      uid: user?.id ?? '',
                    }) || ''
                  }
                >
                  <CardMedia
                    component="img"
                    image={b.thumbnail.mediumUri || ''}
                  />
                </CardActionArea>
              ) : (
                <CardActionArea
                  href={
                    replaceParameters(b.link?.innerPath ?? '', {
                      uid: user?.id ?? '',
                    }) || ''
                  }
                >
                  <CardMedia
                    component="img"
                    image={b.thumbnail.mediumUri || ''}
                  />
                </CardActionArea>
              )}
            </Card>
          </Fade>
        ))}
      </Stack>
    </CellFrame>
  )
}

/**
 * 漫画アプリ用のバナー表示
 * こちらはカルーセル
 * https://zpl.io/MdMJ8nd
 */
const MagazineBannersCell: Cell<BannersCellProps> = ({
  banners,
  loading,
  user,
}) => {
  const isMultiple = !!banners && banners?.length > 1

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          width: '90vw',
          maxWidth: '600px',
          margin: '8px auto 0',
          borderRadius: 2,
          height: 'calc(90vw * 184 / 335)',
          maxHeight: 'calc(540px * 184 / 335)',
        }}
      />
    )
  }

  return (
    <CellFrame
      sx={{
        px: 5,
        py: 5,
        '.swiper-pagination-bullet-active': { bgcolor: 'grey.700' },
      }}
    >
      <Swiper
        pagination={{ clickable: true }}
        modules={[Pagination]}
        loop={isMultiple}
        slidesPerView={1.2}
        centeredSlides
        spaceBetween={10}
        enabled={isMultiple}
        style={{ paddingBottom: isMultiple ? 40 : 0 }}
      >
        {banners.map((b, i) => (
          <SwiperSlide key={i}>
            <Card>
              {b.link?.externalUrl ? (
                <CardActionArea
                  component="a"
                  target={'_blank'}
                  href={
                    replaceParameters(b.link?.externalUrl, {
                      uid: user?.id ?? '',
                    }) || ''
                  }
                >
                  <CardMedia
                    component="img"
                    image={b.thumbnail.mediumUri || ''}
                  />
                </CardActionArea>
              ) : (
                <CardActionArea
                  href={
                    replaceParameters(b.link?.innerPath ?? '', {
                      uid: user?.id ?? '',
                    }) || ''
                  }
                >
                  <CardMedia
                    component="img"
                    height={140}
                    image={b.thumbnail.mediumUri || ''}
                  />
                </CardActionArea>
              )}
            </Card>
          </SwiperSlide>
        ))}
      </Swiper>
    </CellFrame>
  )
}

type MembershipCellProps = {
  loading: State['loading']
  membership: State['membership']
  artistId?: string
  content?: string
  isMember: boolean
  isPersonalInformationRequired: boolean
  isMembershipPersonalInformationRequired: boolean
}
const MembershipCell: Cell<MembershipCellProps> = ({
  loading,
  membership,
  artistId,
  content,
  isMember,
  isPersonalInformationRequired,
  isMembershipPersonalInformationRequired,
}) => {
  const { t } = useTranslation('artistTop')
  const { routerPages, generatePath } = useRouter()

  if (loading)
    return (
      <CellFrame sx={{ px: 5, pt: 6 }}>
        <SkeletonText variant="medium" width="50%" sx={{ pb: 1 }} />
        <SkeletonText variant="small" numberOfLine={3} lastWidth="75%" />
      </CellFrame>
    )

  if (!membership || !config.modules.membership) return null

  // ろこまこあこアプリの場合、メンバーシップの詳細項目を非表示
  if (config.applicationKey === 'rocomacoaco') {
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 5, pb: 8 }}
      >
        <Button
          size="large"
          href={
            artistId
              ? generatePath(routerPages.ArtistMembership, {
                  artistId,
                })
              : undefined
          }
          sx={{ mx: 'auto', width: 244, mt: 6 }}
          color={isMember ? 'inherit' : 'primary'}
        >
          {t(isMember ? 'seeOther' : 'seeMember')}
        </Button>
      </Stack>
    )
  }

  return (
    <Fade in>
      <CellFrame sx={{ px: 5, pb: 6 }}>
        <Card variant="outlined" sx={{ p: 5 }}>
          <CardContent sx={{ p: 0 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5">{t('benefit')}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {isMember &&
                  isPersonalInformationRequired &&
                  isMembershipPersonalInformationRequired && (
                    <UnregisteredInfoLabel />
                  )}
                <PlanLabel
                  planRelationship={isMember ? 'joined' : 'unjoined'}
                />
              </Stack>
            </Stack>
            <Typography variant="body2" sx={{ mt: 3 }}>
              {content}
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              size="large"
              href={
                artistId
                  ? generatePath(routerPages.ArtistMembership, {
                      artistId,
                    })
                  : undefined
              }
              sx={{ mx: 'auto', width: 244, mt: 6 }}
              color={isMember ? 'inherit' : 'primary'}
            >
              {t(isMember ? 'seeOther' : 'becomeMember')}
            </Button>
          </CardActions>
        </Card>
      </CellFrame>
    </Fade>
  )
}

type NotificationListsCellProps = {
  notifications: OrganizationNotificationConnectionFragment['edges']
  artistId: string
}
const NotificationListsCell: Cell<NotificationListsCellProps> = ({
  notifications,
  artistId,
}) => {
  const { t } = useTranslation('artistTop')
  const { generatePath, routerPages } = useRouter()
  return (
    <CellFrame sx={{ px: 5, pt: 4, pb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2 }}
      >
        <Typography variant="h3">{t('officialNotificationLabel')}</Typography>
        <Link
          variant="h6"
          href={generatePath(routerPages.ArtistOfficialInformation, {
            artistId,
          })}
        >
          {t('toList')}
        </Link>
      </Stack>
      <List>
        {notifications?.map(({ node }, index) => (
          <NotificationItemCell
            key={node.id}
            notification={node}
            divider={notifications?.length - 1 > index}
            isExternalLink={node.link ? isExternalLink(node.link) : false}
          />
        ))}
      </List>
    </CellFrame>
  )
}

type NotificationItemCellProps = {
  notification: OrganizationNotificationBoxFragment
  divider: boolean
  isExternalLink: boolean
}
const NotificationItemCell: Cell<NotificationItemCellProps> = ({
  notification,
  divider,
  isExternalLink,
}) => {
  const { generatePath, routerPages } = useRouter()
  const { onClick } = useBrowserLink(notification.link ?? '')

  return (
    <ListItemButton
      component={isExternalLink ? 'p' : Link}
      href={
        notification.link ||
        generatePath(routerPages.OfficialInformationDetail, {
          notificationId: notification.id,
        })
      }
      onClick={isExternalLink ? onClick : undefined}
      sx={{ height: 'auto', py: 3, px: 0 }}
      divider={divider}
    >
      <ListItemText
        primary={notification.title}
        primaryTypographyProps={{
          variant: 'body2',
          sx: styleSets.lineCamp(2),
        }}
      />
      <ListItemIcon>
        <Icons.Transition fontSize="xSmall" />
      </ListItemIcon>
    </ListItemButton>
  )
}

const NewStoresCellWithAlgolia = connectInfiniteHits<
  InfiniteHitsProvided<Omit<StoreItemFragment, 'id'> & { objectID: string }> & {
    artistId: NewStoresCellProps['artistId']
    loading: NewStoresCellProps['loading']
    onSelectItem: NewStoresCellProps['onSelectItem']
    onToList: NewStoresCellProps['onToList']
  },
  {}
>(({ loading, hits, artistId, onSelectItem, onToList }) => {
  const newStores = hits.map(({ objectID, ...item }) => ({
    id: objectID,
    ...item,
  }))

  // アイテムがない場合、ストア項目を非表示
  if (!newStores.length) return <></>

  return (
    <NewStoresCell
      loading={loading}
      newStores={newStores}
      artistId={artistId}
      onSelectItem={onSelectItem}
      onToList={onToList}
    />
  )
})

type NewStoresCellProps = {
  loading: State['loading']
  newStores: StoreItemFragment[]
  artistId: string
  onSelectItem: () => void
  onToList: () => void
}
const NewStoresCell: Cell<NewStoresCellProps> = ({
  loading,
  newStores,
  artistId,
  onSelectItem,
  onToList,
}) => {
  const { t } = useTranslation('artistTop')
  const { routerPages, generatePath } = useRouter()

  return (
    <HorizontalList
      loading={loading}
      loadingItemLength={7}
      title={t('newStore')}
      spacing={1}
      transTypographyProps={{ onClick: onToList }}
      listHref={generatePath(routerPages.ArtistStore, { artistId })}
      items={newStores}
      trans={t('toList')}
      LoadingComponent={() => <StoreItemAreaSkeleton size="small" />}
      renderItem={(item, i) => (
        <StoreItemArea
          item={item}
          size="small"
          key={i}
          artistId={artistId}
          onClick={onSelectItem}
        />
      )}
    />
  )
}

export const StoreStateResults = connectStateResults<
  Partial<
    StateResultsProvided<
      Omit<StoreItemFragment, 'id'> & { objectID: string }
    > & {
      onCompleted: (
        items: StoreItemFragment[],
        options: { page: number }
      ) => void
    }
  >
>(({ searchResults, onCompleted }) => {
  useEffect(() => {
    if (searchResults?.hits?.length != undefined) {
      const items = searchResults.hits.map(({ objectID, ...item }) => ({
        id: objectID,
        ...item,
      }))
      onCompleted &&
        onCompleted(items, { page: (searchResults?.page ?? 0) + 1 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults?.page, searchResults?.hits?.length, onCompleted])

  return null
})

type NewGachasCellProps = {
  loading: State['loading']
  newGachas: State['newGachas']
  artistId: string
}
const NewGachasCell: Cell<NewGachasCellProps> = ({
  loading,
  newGachas,
  artistId,
}) => {
  const { t } = useTranslation('artistTop')
  const { routerPages, generatePath } = useRouter()

  return (
    <>
      {loading || newGachas?.length ? (
        <HorizontalList
          loading={loading}
          title={t('gachaLabel')}
          listHref={
            artistId && generatePath(routerPages.ArtistGachas, { artistId })
          }
          items={newGachas}
          trans={t('toList')}
          LoadingComponent={GachaBoxSkeleton}
          renderItem={(gacha, i) => (
            <Fade key={i} in>
              <GachaBox gacha={gacha} artistId={artistId} />
            </Fade>
          )}
        />
      ) : null}
    </>
  )
}

type NewMissionsCellProps = {
  loading: State['loading']
  newMissions: State['newMissions']
  artistId?: string
}
const NewMissionsCell: Cell<NewMissionsCellProps> = ({
  loading,
  newMissions,
  artistId,
}) => {
  const { t } = useTranslation('artistTop')
  const { analytics } = useAnalytics()
  const { routerPages, generatePath } = useRouter()

  if (!newMissions.length || !config.modules.mission) return null

  return (
    <HorizontalList
      loading={loading}
      title={t('missionLabel')}
      listHref={
        artistId && generatePath(routerPages.ArtistMissions, { artistId })
      }
      items={newMissions}
      trans={t('toList')}
      LoadingComponent={MissionBoxSkeleton}
      renderItem={(mission, i) => (
        <Fade key={i} in>
          <MissionBox
            sx={{ mt: 0 }}
            mission={mission}
            artistId={artistId}
            onClick={() => {
              analytics?.track('click_select', {
                applicationKey: config.applicationKey,
                artistId: mission?.artist.id,
                missionId: mission?.id,
                type: mission?.type,
                name: 'missions',
              })
            }}
          />
        </Fade>
      )}
    />
  )
}

type NewEventsCellProps = {
  artistId: State['artistId']
  loading: State['loading']
  events: State['events']
}
const NewEventsCell: Cell<NewEventsCellProps> = ({
  artistId,
  events,
  loading,
}) => {
  const { t } = useTranslation('artistTop')
  const { routerPages, generatePath } = useRouter()

  if ((!events?.edges?.length && !loading) || !config.modules.event) return null

  return (
    <HorizontalList
      loading={loading}
      spacing={4}
      title={t('newEventsLabel')}
      listHref={generatePath(routerPages.ArtistEvents, { artistId })}
      items={events?.edges ?? []}
      trans={t('toList')}
      LoadingComponent={() => <EventCardSkeleton width={315} />}
      renderItem={(event, i) => (
        <Fade key={i} in>
          <EventCard event={event.node} sx={{ width: 315, height: '100%' }} />
        </Fade>
      )}
    />
  )
}

type NewPostsCellProps = {
  loading: State['loading']
  newPosts: State['newPosts']
  artistId?: string
  isMembershipPersonalInformationRequired: boolean
  onLikePost: (
    postId: string,
    isLike: boolean,
    likeCount: number
  ) => Promise<void>
}
const NewPostsCell: Cell<NewPostsCellProps> = ({
  loading,
  newPosts,
  artistId,
  onLikePost,
  isMembershipPersonalInformationRequired,
}) => {
  const { t } = useTranslation('artistTop')
  const { routerPages, generatePath } = useRouter()

  if (!newPosts.length) return <></>

  return (
    <HorizontalList
      loading={loading}
      title={t('postLabel')}
      listHref={artistId && generatePath(routerPages.ArtistPosts, { artistId })}
      items={newPosts}
      trans={t('toList')}
      LoadingComponent={PostBoxSkeleton}
      renderItem={(p, i) => (
        <Fade key={i} in>
          <PostBox
            post={p}
            sx={{ height: 356 }}
            onLike={() => onLikePost(p.id, p.isLike, p.likeCount)}
            isMembershipPersonalInformationRequired={
              isMembershipPersonalInformationRequired
            }
          />
        </Fade>
      )}
    />
  )
}

type NewThreadsCellProps = {
  loading: State['loading']
  newThreads: State['newThreads']
  artistId?: string
  isMember: boolean
  isShowModalPersonalInfoRequire: boolean
}
const NewThreadsCell: Cell<NewThreadsCellProps> = ({
  loading,
  newThreads,
  artistId,
  isMember,
  isShowModalPersonalInfoRequire,
}) => {
  const { t } = useTranslation('artistTop')
  const { routerPages, generatePath } = useRouter()

  if (!config.modules.thread) return null

  return (
    <>
      {loading || newThreads.length ? (
        <HorizontalList
          loading={loading}
          loadingItemLength={3}
          title={t('communityLabel')}
          listHref={
            artistId && generatePath(routerPages.ArtistThreads, { artistId })
          }
          LoadingComponent={() => <ThreadBoxSkeleton width={315} />}
          items={newThreads}
          trans={t('toList')}
          renderItem={(thread, i) => (
            <ThreadBox
              key={i}
              thread={thread}
              hasPatron={isMember}
              isShowModalPersonalInfoRequire={isShowModalPersonalInfoRequire}
              width={315}
              fixedHeight
            />
          )}
        />
      ) : null}
    </>
  )
}

export const Empty: FC<{ title?: string; description?: string }> = ({
  title,
  description,
}) => {
  const { t } = useTranslation()
  return (
    <Box sx={{ px: 6, py: 5, textAlign: 'center' }}>
      {title && (
        <Typography variant="h5" color="text.secondary">
          {title}
        </Typography>
      )}
      <Typography variant="body1" color="text.secondary">
        {description || t('emptyMessage')}
      </Typography>
    </Box>
  )
}

type MembershipOnlyModalCellProps = {
  open: boolean
  onClose: () => void
  artistId: string
  displayName: string
}
const MembershipOnlyModalCell: Cell<MembershipOnlyModalCellProps> = ({
  open,
  onClose,
  artistId,
  displayName,
}) => {
  const { t } = useTranslation('artistTop')
  const { generatePath, routerPages } = useRouter()

  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('liveViewMemberTitle')}
      content={t('liveViewMemberDesc', { displayName })}
      cancelText={t('cancel')}
      buttons={
        <Button
          size="large"
          sx={{ width: 212 }}
          href={generatePath(routerPages.ArtistMembership, { artistId })}
        >
          {t('seeMore')}
        </Button>
      }
    />
  )
}

type BlockedArtistCellProps = {
  loading: State['loading']
  artist: State['artist']
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  followArtists: Maybe<SideMenuProps['followArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
  onUnBlock: Action['onUnBlock']
}
const BlockedArtistCell: Cell<BlockedArtistCellProps> = ({
  loading,
  artist,
  followArtists,
  memberArtists,
  user,
  isArtist,
  onUnBlock,
}) => {
  const [{ bgImage }] = useArtistThemeContext()

  if (loading) {
    return (
      <CellFrame>
        <Skeleton
          variant="rectangular"
          width="100%"
          sx={{
            height: '100vw',
            maxHeight: 744,
            pt: styles.safeAreaTop,
            '& header': { visibility: 'visible' },
          }}
        >
          <BlockedArtistInfoCell
            artist={artist}
            followArtists={followArtists}
            memberArtists={memberArtists}
            user={user}
            isArtist={isArtist}
            onUnBlock={onUnBlock}
          />
        </Skeleton>
      </CellFrame>
    )
  }

  return (
    <HeaderImageArea
      hasSafeArea
      gradationMask
      bgImage={bgImage}
      imageUri={artist?.avatar?.mediumUri}
    >
      <Fade sx={{ height: '100%' }} in>
        <BlockedArtistInfoCell
          artist={artist}
          followArtists={followArtists}
          memberArtists={memberArtists}
          user={user}
          isArtist={isArtist}
          onUnBlock={onUnBlock}
        />
      </Fade>
    </HeaderImageArea>
  )
}

type BlockedArtistInfoCellProps = {
  artist: State['artist']
  memberArtists: SideMenuProps['memberArtists']
  followArtists: SideMenuProps['followArtists']
  user: SideMenuProps['user']
  isArtist: SideMenuProps['isArtist']
  onUnBlock: Action['onUnBlock']
}
const BlockedArtistInfoCell: Cell<BlockedArtistInfoCellProps> = ({
  artist,
  memberArtists,
  followArtists,
  user,
  isArtist,
  onUnBlock,
}) => {
  const { t } = useTranslation('artistTop')
  const { palette } = useTheme()
  const { routerPages, generatePath } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])
  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])
  const open = Boolean(anchorEl)
  const id = open ? 'unblock-popover' : undefined

  return (
    <Stack justifyContent="space-between" sx={{ height: '100%' }}>
      <Stack>
        <Header
          color="transparent"
          elevation={0}
          sx={{ backgroundColor: 'transparent' }}
          left={
            config.applicationType === 'organization' && (
              <IconButton.AvatarNoImage
                edge="start"
                onClick={() => setOpenSideMenu(!openSideMenu)}
                color="icon.line"
                bgcolor="icon.background"
                badge={
                  user?.hasManagementTask || !!user?.unreadNotificationCount
                }
              />
            )
          }
          right={
            <>
              {user && config.applicationType === 'standalone' && (
                <Stack direction="row" spacing={3}>
                  {config.modules.campaign && config.modules.mission && (
                    <IconButton.Event
                      color="icon.line"
                      bgcolor="icon.background"
                      size="medium"
                      href={generatePath(routerPages.GlobalEvents)}
                    />
                  )}
                  <IconButton.Notice
                    color="icon.line"
                    bgcolor="icon.background"
                    size="medium"
                    href={routerPages.Notifications}
                  />
                </Stack>
              )}
            </>
          }
        />
        <SideMenu
          followArtists={followArtists}
          memberArtists={memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={user}
          isArtist={isArtist}
        />
      </Stack>
      {config.applicationType === 'organization' && (
        <Stack sx={{ px: 5, mb: 3, zIndex: 2 }}>
          <Typography color="text.primary" variant="h1" sx={{ mb: 1 }}>
            {artist?.displayName}
          </Typography>
          <Typography color="text.secondary" variant="caption" sx={{ mb: 4 }}>
            {artist?.formattedAccountId}
          </Typography>
          <Box>
            <Button
              aria-describedby={id}
              variant="outlined"
              size="small"
              color="error"
              /**
               * @todo
               * ブロック解除API本格実装後コメントアウト
               * https://app.clickup.com/t/908127/UT-8682
               * onClick={handleClick}
               */
              sx={{
                color: palette?.error?.main,
                borderColor: palette?.error?.main,
              }}
            >
              {t('blocking')}
            </Button>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              onClick={() => {
                onUnBlock()
                handleClose()
              }}
            >
              <Typography
                variant="body2"
                sx={{ py: 3, px: 4, bgcolor: 'grey.100' }}
              >
                {t('unBlock', { artistAccountId: artist?.formattedAccountId })}
              </Typography>
            </Popover>
          </Box>
        </Stack>
      )}
    </Stack>
  )
}
